import { createContext, VNode } from 'preact';
import { useCallback, useContext } from 'preact/hooks';
import { signal, Signal, useSignal } from '@preact/signals';
import { logger } from '@accedo/logger';
import { Entitlement, Subscription } from '##/dataModels/subscription';
import { ServiceManager } from '##/services/elevate/manager';

type ReducedEntitlements = Record<string, Record<string, string | number | boolean | void>>;

type SubscriptionContextType = {
  subscriptions: Signal<Subscription[]>;
  entitlements: Signal<ReducedEntitlements>;
  fetchSubscriptions: (token?: string, refresh?: boolean) => Promise<void>;
  fetchEntitlements: (token?: string, refresh?: boolean) => Promise<void>;
};

export const SubscriptionContext = createContext<SubscriptionContextType>({
  subscriptions: signal<Subscription[]>([]),
  entitlements: signal<ReducedEntitlements>({}),
  fetchSubscriptions: () => Promise.resolve(),
  fetchEntitlements: () => Promise.resolve(),
});

const SubscriptionProvider = ({ children }: { children: preact.ComponentChildren }): VNode => {
  const subscriptions = useSignal<Subscription[]>([]);
  const entitlements = useSignal<ReducedEntitlements>({});

  const fetchEntitlements = useCallback(async (token?: string, refresh?: boolean) => {
    if (refresh) {
      await ServiceManager.getSubscriptionService()?.clearSubscriptionsCache();
    }

    if (!token) {
      logger.warn('[SubscriptionProvider] No auth token provided for fetching entitlements.');

      return;
    }

    try {
      const entitlementsResponse = await ServiceManager.getSubscriptionService()?.getEntitlements({ authToken: token });

      const reducedEntitlements = (entitlementsResponse as Entitlement[])?.reduce(
        (acc: ReducedEntitlements, entitlement: Entitlement) => {
          const { category, details, value } = entitlement;

          if (!category) {
            return acc; // Skip if category is not defined
          }

          if (!acc[category]) {
            acc[category] = { [details]: value };
          } else {
            acc[category] = {
              ...acc[category],
              [details]: value,
            };
          }

          return acc;
        },
        {},
      );

      entitlements.value = reducedEntitlements as ReducedEntitlements;
    } catch (error) {
      logger.error('[SubscriptionProvider] Error fetching entitlements:', error);
    }
  }, []);

  const fetchSubscriptions = useCallback(async (token?: string, refresh?: boolean) => {
    if (refresh) {
      await ServiceManager.getSubscriptionService()?.clearSubscriptionsCache();
    }

    if (!token) {
      logger.warn('[SubscriptionProvider] No auth token provided for fetching subscriptions.');

      return;
    }

    try {
      const subs = await ServiceManager.getSubscriptionService()?.getSubscriptions({
        authToken: token,
      });

      subscriptions.value = subs as Subscription[];
    } catch (error) {
      logger.error('[SubscriptionProvider] Error fetching subscriptions:', error);
    }
  }, []);

  return (
    <SubscriptionContext.Provider value={{ subscriptions, entitlements, fetchSubscriptions, fetchEntitlements }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

const useSubscriptionContext = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
};

export { SubscriptionProvider, useSubscriptionContext };
